import { ref, watchEffect } from 'vue'
import { defineStore } from 'pinia';
import { useProductStore } from '@/stores/ProductStore';
import { useMapStore } from  '@/stores/MapStore';
import { useCachedFetch } from '@/composables/useFetch'
import { CanvasGeoJsonOverlay } from '@/utils/CanvasGeoJsonOverlay'
import { NativeGeoJsonOverlay } from '@/utils/NativeGeoJsonOverlay'
import { isWebGLSupported } from '@/utils/webglSupport'
import useMapConfigurationGlobals from '@/composables/useMapConfigurationGlobals'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'

const { $BASEURL } = useConfigurationGlobals();
const { $CUSTOM_LAYER_WEBGL } = useMapConfigurationGlobals()

export let useMapCustomLayerStore = defineStore('mapCustomLayerStore', () => {
  const getFileBaseUrl = `${$BASEURL}/common/getCustomLayer?file=`;
  let map = window.$JWX.map;
  
  const productStore = useProductStore();
  const mapStore = useMapStore();

  const colors = [
    { hex: '#e6194b', label: 'red' },
    { hex: '#009eba', label: 'teal' },
    { hex: '#3cb44b', label: 'green' },
    { hex: '#ffe119', label: 'yellow' },
    { hex: '#4363d8', label: 'blue' },
    { hex: '#f58231', label: 'orange' },
    { hex: '#911eb4', label: 'purple' },
    { hex: '#46f0f0', label: 'cyan' },
    { hex: '#f032e6', label: 'magenta' },
    { hex: '#bcf60c', label: 'lime' },
    { hex: '#fabebe', label: 'pink' },
    { hex: '#008080', label: 'dark-teal' },
    { hex: '#e6beff', label: 'lavender' },
    { hex: '#9a6324', label: 'brown' },
    { hex: '#fffac8', label: 'cream' },
    { hex: '#800000', label: 'maroon' },
    { hex: '#aaffc3', label: 'mint' },
    { hex: '#808000', label: 'olive' }
  ];

  const getSVGMarker = (color) => ({
    path: 'M5.61719 13.68542C6.95313 11.92539 10 7.65967 10 5.26362 10 2.35766 7.76042 0 5 0S0 2.35766 0 5.26362c0 2.39605 3.04688 6.66177 4.38281 8.4218.32031.41944.91406.41944 1.23438 0ZM5 3.50908c.92047 0 1.66667.78554 1.66667 1.75454S5.92047 7.01816 5 7.01816c-.92047 0-1.66667-.78553-1.66667-1.75454 0-.969.7462-1.75454 1.66667-1.75454Z',
    fillColor: color,
    fillOpacity: 0.6,
    strokeWeight: 1.5,
    rotation: 0,
    scale: 2,
    anchor: new google.maps.Point(5, 14)
  })

  const canvasOverlays = [];

  const customLayers = ref([])
  const activeCustomLayers = ref(new Set())
  // const [activeCustomLayers] = useStorageSystem([], 't1ActiveCustomLayers', 'localStorage')
  
  const activateCustomLayer = async (id, fitbounds = false) => {
    const customLayer = customLayers.value.find(layer => layer.id === id);
    let canvasOverlay;
    
    if (!customLayer || customLayer.isActive) return;
    
    customLayer.isActive = true;    
    customLayer.loading = true;
    
    if (customLayer.overlayIndex === undefined || customLayer.overlayIndex === null) {
      const geojson = await getGeoJsonFile(customLayer.url);

      if (!geojson) {
        customLayer.isActive = false
        customLayer.loading = false

        return
      }

      const style = {
        fillColor: customLayer.color,
        strokeColor: customLayer.color,
        strokeWeight: 2,
        fillOpacity: 0.2,
        strokeOpacity: 1,
        visible: true,
        icon: {
          url: customLayer.icon,
          scaledSize: new google.maps.Size(18, 24),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(0, 24),
        },
        zIndex: 21
      }

      canvasOverlay = $CUSTOM_LAYER_WEBGL && isWebGLSupported()
        ? new CanvasGeoJsonOverlay(map, geojson, style)
        : new NativeGeoJsonOverlay(map, geojson, style);

      canvasOverlays.push(canvasOverlay);
      customLayer.overlayIndex = canvasOverlays.length - 1;
    } else {
      canvasOverlay = canvasOverlays[customLayer.overlayIndex];
      canvasOverlay.setMap(map);
    }
    
    if (fitbounds) setLayerBounds(canvasOverlay);
    
    setTimeout(() => (customLayer.loading = false), 1) // Allow time for map with custom layer to render
    activeCustomLayers.value.add(customLayer.id);
    mapStore.state.customLayers.add(customLayer.id);
    mapStore.state.globalOverlays.add('custom-layers');
  }

  const deactivateCustomLayer = (id) => {

    const customLayer = customLayers.value.find(layer => layer.id === id);
    
    if (!customLayer) return;

    customLayer.loading = true;
        
    setTimeout(() => {
      canvasOverlays[customLayer.overlayIndex].setMap(null);

      if (customLayer.clearData) {
        canvasOverlays[customLayer.overlayIndex] = null;
        delete customLayer.overlayIndex;
      }
    }, 1) // Allow time for map to render

    setTimeout(() => {
      customLayer.isActive = false;
      customLayer.loading = false;
      activeCustomLayers.value.delete(customLayer.id);
      mapStore.state.customLayers.delete(customLayer.id);
      if (!mapStore.state.globalOverlays.size) mapStore.state.globalOverlays.delete('custom-layers');
    }, 2) // Allow time for map to render
  }

  const getGeoJsonFile = async (url) => {
    try {
      const { error, data } = await useCachedFetch(url);

      if (error.value) {
        throw error.value
      }

      if (!data) return;

      return data.value;
    } catch (error) {
      console.warn(error);

      return error;
    }
  }

  const getCustomLayers = async (refetch = false) => {
    if (customLayers.value.length > 0 && !refetch) return;

    customLayers.value = [];

    await productStore.getRegionalProducts();

    productStore.customLayers.map((layer, i) => {
      const { name, custom_layer_id, file_name } = layer;

      customLayers.value.push({
        id: custom_layer_id,
        name: name,
        fileName: file_name,
        url: `${getFileBaseUrl}${file_name}`,
        type: 'geojson',
        color: colors[i % colors.length].hex,
        icon: getMarkerIconURL(colors[i % colors.length].label),
        geojson: null,
        isActive: false,
        loading: false
      })
    })
  }

  const setLayerBounds = (canvasOverlay) => {
    const bounds = canvasOverlay.getBounds();
    if (bounds && !bounds.isEmpty()) {
      map.fitBounds(bounds);
    }
  }

  const activateSavedCustomLayers = async () => {
    if (!mapStore.state.globalOverlays.has('custom-layers') || !mapStore.state.customLayers.size) return;

    await getCustomLayers();

    mapStore.state.customLayers.forEach((layer) => activateCustomLayer(layer))
  }

  const setMapVar = (mapRef) => {
    map = mapRef;
  }

  const getMarkerIconURL = (color) => `https://cdn-consumer.weatherflow.com/jWeather/images/custom-layers/spot-markers/custom-layer-spot-marker-${color}@2x.png`

  watchEffect(() => {
      activeCustomLayers.value.forEach((id) => !mapStore.state.customLayers.has(id) && deactivateCustomLayer(id));

      mapStore.state.customLayers.forEach((id) => !activeCustomLayers.value.has(id) && activateCustomLayer(id));
  });

  return {
    activeCustomLayers,
    customLayers,
    activateCustomLayer,
    activateSavedCustomLayers,
    deactivateCustomLayer,
    getCustomLayers,
    setMapVar
  }
});
